// frontend/src/components/driver-availability/DriverAvailabilityCard.jsx
//
// On-duty status card shown at the top of DriverDashboard.
// See backend/docs/2026-03-30-DRIVER_SHIFT_SCHEDULE_DESIGN.md.

import React, { useCallback, useEffect, useState } from 'react';
import { Clock, LogIn, LogOut, Edit2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from '../../axiosConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { ThemedCard, ThemedButton, ThemedBadge } from '../themed';
import { formatDateAwareTime } from '../../utils/dateTimeFormatter';
import { useDateTimePreferences } from '../../hooks/useDateTimePreferences';
import PunchInDialog from './PunchInDialog';
import ConfirmDialogShell from '../dialogs/ConfirmDialogShell';

const DriverAvailabilityCard = ({ entityId }) => {
    const { t } = useTranslation();
    const { text, cn } = useTheme();
    const { dateFormat, timeFormat } = useDateTimePreferences();
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('punch_in'); // 'punch_in' | 'extend'
    const [showPunchOutConfirm, setShowPunchOutConfirm] = useState(false);

    const fetchStatus = useCallback(async () => {
        if (!entityId) return;
        try {
            const res = await axios.get(`/api/driver-availability/${entityId}/me`);
            setSession(res.data?.data?.active_session || null);
        } catch (err) {
            console.error('Failed to load availability status', err);
        } finally {
            setLoading(false);
        }
    }, [entityId]);

    useEffect(() => { fetchStatus(); }, [fetchStatus]);

    const openPunchIn = () => { setDialogMode('punch_in'); setDialogOpen(true); };
    const openExtend = () => { setDialogMode('extend'); setDialogOpen(true); };

    const handleDialogSubmit = async ({ expectedEndAt, routeId }) => {
        setSubmitting(true);
        try {
            const body = { expected_end_at: expectedEndAt, route_id: routeId ?? null };
            const url = dialogMode === 'punch_in'
                ? `/api/driver-availability/${entityId}/punch-in`
                : `/api/driver-availability/${entityId}/current-session`;
            const res = dialogMode === 'punch_in'
                ? await axios.post(url, body)
                : await axios.put(url, body);
            setSession(res.data?.data || null);
            setDialogOpen(false);
            toast.success(dialogMode === 'punch_in'
                ? t('driverAvailability.punchedIn')
                : t('driverAvailability.endTimeUpdated'));
        } catch (err) {
            const code = err?.response?.data?.code;
            if (code === 'ALREADY_ON_DUTY') {
                toast.warn(t('driverAvailability.alreadyOnDuty'));
                await fetchStatus();
                setDialogOpen(false);
            } else if (code === 'END_TIME_TOO_SOON') {
                toast.error(t('driverAvailability.minEndTimeError'));
            } else {
                toast.error(err?.response?.data?.error || t('driverAvailability.requestFailed'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handlePunchOut = async () => {
        setSubmitting(true);
        try {
            const res = await axios.post(`/api/driver-availability/${entityId}/punch-out`);
            const released = res.data?.data?.unassigned_trips || [];
            setSession(null);
            if (released.length > 0) {
                toast.info(t('driverAvailability.confirmPunchOutWithAssignments', { count: released.length }));
            } else {
                toast.success(t('driverAvailability.punchedOut'));
            }
        } catch (err) {
            const code = err?.response?.data?.code;
            if (code === 'NOT_ON_DUTY') {
                toast.warn(t('driverAvailability.notOnDuty'));
                await fetchStatus();
            } else {
                toast.error(err?.response?.data?.error || t('driverAvailability.requestFailed'));
            }
        } finally {
            setSubmitting(false);
            setShowPunchOutConfirm(false);
        }
    };

    if (loading) {
        return (
            <ThemedCard className="mb-6">
                <ThemedCard.Body>
                    <div className={cn('text-center', text.muted)}>...</div>
                </ThemedCard.Body>
            </ThemedCard>
        );
    }

    return (
        <>
            <ThemedCard className="mb-6">
                <ThemedCard.Body>
                    {session ? (
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                    <ThemedBadge variant="success">{t('driverAvailability.onDuty')}</ThemedBadge>
                                    <span className={cn('text-sm', text.muted)}>
                                        <Clock className="inline h-4 w-4 mr-1" />
                                        {t('driverAvailability.onDutySince', {
                                            time: formatDateAwareTime(session.started_at, dateFormat, timeFormat)
                                        })}
                                    </span>
                                </div>
                                <div className={cn('text-sm', text.secondary)}>
                                    {t('driverAvailability.expectedUntil', {
                                        time: formatDateAwareTime(session.expected_end_at, dateFormat, timeFormat)
                                    })}
                                </div>
                                {session.route_name && (
                                    <div className={cn('text-sm', text.muted)}>
                                        {t('driverAvailability.preferredRouteLabel')}: {session.route_name}
                                    </div>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <ThemedButton variant="secondary" onClick={openExtend} disabled={submitting}>
                                    <Edit2 className="h-4 w-4 mr-1" />
                                    {t('driverAvailability.extendEndTime')}
                                </ThemedButton>
                                <ThemedButton variant="danger" onClick={() => setShowPunchOutConfirm(true)} disabled={submitting}>
                                    <LogOut className="h-4 w-4 mr-1" />
                                    {t('driverAvailability.punchOut')}
                                </ThemedButton>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div className={cn('text-sm', text.secondary)}>
                                {t('driverAvailability.offDuty')}
                            </div>
                            <ThemedButton variant="primary" onClick={openPunchIn} disabled={submitting}>
                                <LogIn className="h-4 w-4 mr-1" />
                                {t('driverAvailability.punchIn')}
                            </ThemedButton>
                        </div>
                    )}
                </ThemedCard.Body>
            </ThemedCard>

            {dialogOpen && (
                <PunchInDialog
                    mode={dialogMode}
                    entityId={entityId}
                    initialRouteId={session?.route_id ?? null}
                    initialExpectedEndAt={session?.expected_end_at}
                    submitting={submitting}
                    onCancel={() => setDialogOpen(false)}
                    onSubmit={handleDialogSubmit}
                />
            )}

            <ConfirmDialogShell
                isOpen={showPunchOutConfirm}
                onClose={() => !submitting && setShowPunchOutConfirm(false)}
                onConfirm={handlePunchOut}
                title={t('driverAvailability.punchOut')}
                confirmLabel={t('driverAvailability.punchOut')}
                confirmVariant="danger"
                confirmIcon={LogOut}
                isLoading={submitting}
                isDisabled={submitting}
                showIcon={true}
                iconVariant="warning"
                closeOnBackdropClick={!submitting}
            >
                <p className={cn('text-sm', text.secondary)}>
                    {t('driverAvailability.confirmPunchOut')}
                </p>
            </ConfirmDialogShell>
        </>
    );
};

export default DriverAvailabilityCard;
